import React, { useRef } from 'react';

import { useIsOverflow } from '../hooks/useIsOverflow';
import { type TooltipPosition, Tooltip } from '../Tooltip';
import { type TextProps, Text } from '../Typography';
import {
  lineClampStyles,
  textStyles,
  tooltipBodyStyles,
} from './OverflowTooltipText.styles';

export type OverflowTooltipTextProps = {
  /**
   * Full text shown in the tooltip when truncated, and as the default
   * visible content when `children` is omitted.
   */
  text: string;
  /**
   * Optional rich content inside the truncating container. Prefer inline
   * nodes — block children can break ellipsis / line-clamp.
   */
  children?: React.ReactNode;
  /**
   * Max visible lines. `1` (default) uses single-line ellipsis; `> 1`
   * uses `-webkit-line-clamp`.
   */
  lines?: number;
  /**
   * Text size when rendering the default `text` content.
   */
  size?: TextProps['size'];
  /**
   * Font weight when rendering the default `text` content.
   */
  fontWeight?: TextProps['fontWeight'];
  /**
   * Tooltip placement.
   */
  tooltipPosition?: TooltipPosition;
  /**
   * Additional CSS class names on the tooltip trigger wrapper.
   */
  className?: string;
};

const OverflowTooltipText = ({
  text,
  children,
  lines = 1,
  size = 'sm',
  fontWeight = 'normal',
  tooltipPosition = 'top',
  className,
}: OverflowTooltipTextProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const isOverflow = useIsOverflow(textRef, [text, lines, children]);
  const useLineClamp = lines > 1;

  return (
    <Tooltip
      content={<span css={tooltipBodyStyles}>{text}</span>}
      position={tooltipPosition}
      disabled={!isOverflow || !text}
      fullWidth
      className={className}
    >
      <div
        ref={textRef}
        css={[textStyles, useLineClamp && lineClampStyles]}
        style={useLineClamp ? { WebkitLineClamp: lines } : undefined}
      >
        {children ?? (
          <Text as="span" size={size} fontWeight={fontWeight} textWrap="wrap">
            {text}
          </Text>
        )}
      </div>
    </Tooltip>
  );
};

OverflowTooltipText.displayName = 'OverflowTooltipText';

export default OverflowTooltipText;
